/**
 * Heuro Cinema - Main Entry Point
 * Orchestrates the complete cinematic experience.
 */

import { fetchPropertyConfig, getPropertyIdFromHost } from './api/client';
import type { PropertyConfig } from './types/content';
import { initGraph } from './components/graph';
import { initTheme, setGraphInitialized } from './components/theme';
import { initLanguageSelection, setOnLanguageSelected, isLanguageSelected } from './components/language';
import { setupCardScrimListener, collapseCard, hasExpandedCard, updateCardContent } from './components/cards';
import { runFullTimeline, togglePlayback, pauseTimeline, setTimelineLanguage, setTimelineTranslations } from './components/timeline';
import { initModal, showModal, updateModalI18n } from './components/modal';

let currentConfig: PropertyConfig | null = null;
let availableLanguages: string[] = [];
const propertyId = getPropertyIdFromHost();

async function fetchConfigForLang(lang: string): Promise<PropertyConfig> {
  return fetchPropertyConfig(propertyId, lang);
}

function applyTimingConfig(timing: Record<string, unknown>): void {
  const root = document.documentElement;
  // Apply timing values as CSS variables (snake_case from API)
  if (timing.card_enter_duration != null) {
    root.style.setProperty('--card-enter-duration', `${timing.card_enter_duration}ms`);
  }
  if (timing.card_flip_delay != null) {
    root.style.setProperty('--card-flip-delay', `${timing.card_flip_delay}ms`);
  }
  if (timing.card_dock_duration != null) {
    root.style.setProperty('--card-dock-duration', `${timing.card_dock_duration}ms`);
  }
  console.log('Applied timing config:', timing);
}

function updateUI(config: PropertyConfig): void {
  // Apply timing settings
  if (config.timing) {
    applyTimingConfig(config.timing);
  }

  // Hero
  const heroTitle = document.getElementById('heroTitle');
  const heroSubtitle = document.getElementById('heroSubtitle');
  if (heroTitle) heroTitle.textContent = config.i18n.hero.title;
  if (heroSubtitle) heroSubtitle.textContent = config.i18n.hero.subtitle;

  // CTA
  const ctaQuestion = document.getElementById('ctaQuestion');
  const ctaButton = document.getElementById('ctaButton');
  if (ctaQuestion) ctaQuestion.textContent = config.cta.question;
  if (ctaButton) ctaButton.textContent = config.cta.button;

  // Playback hint
  const playbackHint = document.getElementById('playbackHint');
  if (playbackHint) playbackHint.textContent = config.i18n.playback.pause;

  // Current language indicator
  const currentLang = document.getElementById('currentLang');
  if (currentLang) currentLang.textContent = config.i18n.meta.label;

  // Footer
  const impressumLink = document.getElementById('impressumLink');
  const privacyLink = document.getElementById('privacyLink');
  const footerCopyright = document.getElementById('footerCopyright');

  if (impressumLink) impressumLink.textContent = config.i18n.footer?.impressum || 'Impressum';
  if (privacyLink) privacyLink.textContent = config.i18n.footer?.privacy || 'Privacy';
  if (footerCopyright) {
    const year = new Date().getFullYear();
    footerCopyright.textContent = `© ${year} Heuro Labs GmbH`;
  }

  // Modal
  updateModalI18n(config.i18n.modal);

  // Update timeline translations
  setTimelineLanguage(config.currentLanguage);
  setTimelineTranslations({
    playPause: config.i18n.playback.pause,
    playResume: config.i18n.playback.resume,
  });
}

async function startCinematicExperience(lang: string): Promise<void> {
  // Fetch config for selected language
  currentConfig = await fetchConfigForLang(lang);
  updateUI(currentConfig);

  // Show graph
  const graphContainer = document.getElementById('graphContainer');
  graphContainer?.classList.add('visible');
  initGraph();
  setGraphInitialized(true);

  // Wait for graph to fade in
  await new Promise(resolve => setTimeout(resolve, 800));

  // Show UI elements
  const logo = document.getElementById('logo');
  const centerOverlay = document.getElementById('centerOverlay');
  const playbackHint = document.getElementById('playbackHint');
  const footer = document.getElementById('footer');
  const currentLangEl = document.getElementById('currentLang');

  logo?.classList.add('visible');
  centerOverlay?.classList.add('visible');
  playbackHint?.classList.add('visible');
  footer?.classList.add('visible');
  currentLangEl?.classList.add('visible');

  // Run the timeline
  runFullTimeline(currentConfig.pillars);
}

function closeAllModals(): void {
  document.querySelectorAll('.modal-overlay').forEach(modal => {
    modal.classList.remove('visible');
  });
  // Also close language switcher
  document.getElementById('langSwitcher')?.classList.remove('visible');
}

async function switchLanguage(newLang: string): Promise<void> {
  if (!currentConfig || currentConfig.currentLanguage === newLang) return;

  try {
    // Fetch new config
    const newConfig = await fetchConfigForLang(newLang);
    currentConfig = newConfig;

    // Update all UI text
    updateUI(newConfig);

    // Update card content
    for (const pillar of newConfig.pillars) {
      // Update main card
      updateCardContent(pillar.id, 0, pillar.main);
      // Update split cards
      pillar.splits.forEach((split, i) => {
        updateCardContent(pillar.id, i + 1, split);
      });
    }

    // Close switcher
    document.getElementById('langSwitcher')?.classList.remove('visible');

  } catch (error) {
    console.error('Failed to switch language:', error);
  }
}

function showLanguageSwitcher(): void {
  let switcher = document.getElementById('langSwitcher');

  if (!switcher) {
    // Create switcher element
    switcher = document.createElement('div');
    switcher.id = 'langSwitcher';
    switcher.className = 'lang-switcher';

    const buttons = availableLanguages.map(lang => {
      const isActive = currentConfig?.currentLanguage === lang;
      return `<button class="lang-switch-btn ${isActive ? 'active' : ''}" data-lang="${lang}">${lang.toUpperCase()}</button>`;
    }).join('');

    switcher.innerHTML = buttons;
    document.body.appendChild(switcher);

    // Add click handlers
    switcher.querySelectorAll('.lang-switch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const lang = (btn as HTMLElement).dataset.lang;
        if (lang) switchLanguage(lang);
      });
    });
  } else {
    // Update active state
    switcher.querySelectorAll('.lang-switch-btn').forEach(btn => {
      const lang = (btn as HTMLElement).dataset.lang;
      btn.classList.toggle('active', lang === currentConfig?.currentLanguage);
    });
  }

  switcher.classList.toggle('visible');
}

function initCookieConsent(): void {
  const banner = document.getElementById('cookieBanner');
  const acceptBtn = document.getElementById('cookieAccept');
  const privacyLink = document.getElementById('cookiePrivacyLink');

  // Check if consent was already given
  const hasConsent = localStorage.getItem('cookie-consent');

  if (!hasConsent && banner) {
    // Show banner immediately (legal requirement)
    banner.classList.add('visible');
  }

  // Accept button
  acceptBtn?.addEventListener('click', () => {
    localStorage.setItem('cookie-consent', 'accepted');
    banner?.classList.remove('visible');
    setTimeout(() => {
      banner?.classList.add('hidden');
    }, 400);
  });

  // Privacy link in cookie banner
  privacyLink?.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('privacyModal')?.classList.add('visible');
  });
}

function setupEventListeners(): void {
  // Global click for pause/resume (desktop only — too confusing on mobile)
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Ignore clicks on interactive elements (including cookie banner and lang switcher)
    if (target.closest('.lang-phrase, .theme-btn, .cta-button, .modal, .footer-link, .current-lang, .lang-switcher, .card, .card-scrim, .logo, .cookie-banner')) {
      return;
    }

    if (!isLanguageSelected()) return;

    // On mobile in docked mode, don't toggle playback — let users scroll and tap cards
    if (window.innerWidth < 768 && document.body.classList.contains('mobile-docked')) return;

    const ctaStage = document.getElementById('ctaStage');
    if (ctaStage?.classList.contains('visible')) return;

    togglePlayback();
  });

  // CTA button
  const ctaButton = document.getElementById('ctaButton');
  ctaButton?.addEventListener('click', (e) => {
    e.stopPropagation();
    showModal();
  });

  // Language indicator - show language switcher
  const currentLangEl = document.getElementById('currentLang');
  currentLangEl?.addEventListener('click', (e) => {
    e.stopPropagation();
    showLanguageSwitcher();
  });

  // Impressum link (footer)
  const impressumLink = document.getElementById('impressumLink');
  impressumLink?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeAllModals();
    document.getElementById('impressumModal')?.classList.add('visible');
  });

  // Impressum button (fixed icon)
  const impressumBtn = document.getElementById('impressumBtn');
  impressumBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeAllModals();
    pauseTimeline();
    document.getElementById('impressumModal')?.classList.add('visible');
  });

  // Privacy link
  const privacyLink = document.getElementById('privacyLink');
  privacyLink?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeAllModals();
    document.getElementById('privacyModal')?.classList.add('visible');
  });

  // Close buttons for legal modals
  document.getElementById('impressumClose')?.addEventListener('click', () => {
    document.getElementById('impressumModal')?.classList.remove('visible');
  });
  document.getElementById('privacyClose')?.addEventListener('click', () => {
    document.getElementById('privacyModal')?.classList.remove('visible');
  });

  // Click outside modal to close
  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.remove('visible');
      }
    });
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (hasExpandedCard()) {
        collapseCard();
      } else {
        closeAllModals();
      }
    }
  });

  // Card scrim
  setupCardScrimListener();

}

async function init(): Promise<void> {
  console.log('Heuro Cinema - Initializing...');

  // Initialize theme
  initTheme();

  // Initialize modal
  initModal();

  // Initialize cookie consent
  initCookieConsent();

  // Set up event listeners
  setupEventListeners();

  // Fetch initial config to get available languages
  try {
    const initialConfig = await fetchConfigForLang('en');
    const languages = initialConfig.languages;
    availableLanguages = languages;

    // Set up language selection callback
    setOnLanguageSelected(startCinematicExperience);

    // Start language selection sequence
    await initLanguageSelection(languages, fetchConfigForLang);

  } catch (error) {
    console.error('Failed to load configuration:', error);

    const langSelection = document.getElementById('langSelection');
    if (langSelection) {
      langSelection.innerHTML = `
        <div style="text-align: center; padding: 2rem;">
          <h1 style="font-weight: 200; margin-bottom: 1rem;">Connection Error</h1>
          <p style="color: var(--text-dim);">Failed to load configuration from backend.</p>
          <p style="color: var(--text-dimmer); margin-top: 1rem; font-size: 0.8rem;">
            Make sure the backend is running at ${window.location.origin}/api
          </p>
        </div>
      `;
    }
  }
}

// Start
init();